import React from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  ListGroup, 
  Badge,
  ProgressBar,
  Container 
} from 'react-bootstrap';
import { 
  Truck, 
  Clock, 
  DollarSign, 
  Shield, 
  FileText, 
  UserCheck,
  TrendingUp,
  AlertTriangle
} from 'react-feather';

export default function Home() {
  // Mock data - replace with actual API data
  const stats = {
    deliveries: 12,
    onTimeScore: 94,
    pendingPayouts: 3,
    totalEarnings: 2450
  };

  const quickActions = [
    { icon: <Truck size={16} />, label: 'Today\'s Routes', href: '/routes', variant: 'primary' },
    { icon: <UserCheck size={16} />, label: 'Delivery Confirmations', href: '/confirmations', variant: 'success' },
    { icon: <DollarSign size={16} />, label: 'Payout Status', href: '/payouts', variant: 'warning' },
    { icon: <FileText size={16} />, label: 'Policy Docs', href: '/policies', variant: 'info' }
  ];

  return (
    <Container fluid className="py-4">
      {/* Hero Section */}
      <Card className="hero-card gradient-bg shadow-lg border-0 mb-4 overflow-hidden">
        <div className="position-absolute top-0 end-0 w-50 h-100 bg-gradient-primary opacity-10"></div>
        <Card.Body className="position-relative">
          <Row className="align-items-center g-4">
            <Col xs={12} lg={7}>
              <div className="d-flex align-items-center mb-3">
                <div className="bg-white bg-opacity-20 p-2 rounded-circle me-3">
                  <Truck size={24} className="text-white" />
                </div>
                <h1 className="fw-bold text-white mb-0">Welcome to VMD Partner</h1>
              </div>
              <p className="lead text-white-80 mb-4">
                Your all-in-one platform for managing daily routes, delivery confirmations, 
                payout tracking, and policy references. Optimized for mobile efficiency.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <Button 
                  size="lg" 
                  variant="light" 
                  href="/routes"
                  className="d-flex align-items-center gap-2 fw-semibold"
                >
                  <Truck size={18} />
                  Start Delivery
                </Button>
                <Button 
                  size="lg" 
                  variant="outline-light" 
                  href="/terms"
                  className="d-flex align-items-center gap-2"
                >
                  <FileText size={18} />
                  View Terms
                </Button>
              </div>
            </Col>
            
            <Col xs={12} lg={5}>
              <Card className="stats-card shadow border-0">
                <Card.Body>
                  <h5 className="fw-semibold mb-3 d-flex align-items-center gap-2">
                    <TrendingUp size={18} className="text-primary" />
                    Performance Overview
                  </h5>
                  
                  <ListGroup variant="flush" className="mb-3">
                    <ListGroup.Item className="d-flex justify-content-between align-items-center px-0 py-2 border-bottom">
                      <div className="d-flex align-items-center gap-2">
                        <Truck size={16} className="text-success" />
                        <span>Today's Deliveries</span>
                      </div>
                      <Badge bg="success" pill className="fs-6">
                        {stats.deliveries}
                      </Badge>
                    </ListGroup.Item>
                    
                    <ListGroup.Item className="d-flex justify-content-between align-items-center px-0 py-2 border-bottom">
                      <div className="d-flex align-items-center gap-2">
                        <Clock size={16} className="text-warning" />
                        <span>On-time Score</span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <ProgressBar 
                          now={stats.onTimeScore} 
                          variant={stats.onTimeScore >= 90 ? 'success' : 'warning'}
                          style={{ width: '60px' }}
                        />
                        <Badge bg="light" text="dark" pill>
                          {stats.onTimeScore}%
                        </Badge>
                      </div>
                    </ListGroup.Item>
                    
                    <ListGroup.Item className="d-flex justify-content-between align-items-center px-0 py-2">
                      <div className="d-flex align-items-center gap-2">
                        <DollarSign size={16} className="text-secondary" />
                        <span>Pending Payouts</span>
                      </div>
                      <Badge bg="secondary" pill className="fs-6">
                        ${stats.pendingPayouts * 50}
                      </Badge>
                    </ListGroup.Item>
                  </ListGroup>
                  
                  <div className="bg-light rounded p-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="small text-muted">Total Earnings</span>
                      <span className="fw-bold text-success">${stats.totalEarnings}</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Quick Actions */}
      <Row className="g-3 mb-4">
        <Col xs={12}>
          <h4 className="fw-semibold mb-3">Quick Actions</h4>
          <Row className="g-3">
            {quickActions.map((action, index) => (
              <Col xs={6} md={3} key={index}>
                <Button
                  variant={action.variant}
                  href={action.href}
                  className="w-100 h-100 p-3 d-flex flex-column align-items-center justify-content-center gap-2 shadow-sm border-0"
                >
                  {action.icon}
                  <span className="small fw-semibold">{action.label}</span>
                </Button>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>

      {/* Guidelines & Resources */}
      <Row className="g-4">
        <Col xs={12} lg={6}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Header className="bg-transparent border-0 pb-0">
              <h5 className="fw-semibold d-flex align-items-center gap-2">
                <Shield size={20} className="text-primary" />
                Partner Guidelines
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="d-flex flex-column gap-3">
                <div className="d-flex gap-3">
                  <div className="flex-shrink-0">
                    <div className="bg-primary bg-opacity-10 p-2 rounded">
                      <AlertTriangle size={16} className="text-primary" />
                    </div>
                  </div>
                  <div>
                    <h6 className="fw-semibold mb-1">Cold Chain Management</h6>
                    <p className="small text-muted mb-0">
                      Maintain proper temperature control for dairy products throughout delivery.
                    </p>
                  </div>
                </div>
                
                <div className="d-flex gap-3">
                  <div className="flex-shrink-0">
                    <div className="bg-success bg-opacity-10 p-2 rounded">
                      <UserCheck size={16} className="text-success" />
                    </div>
                  </div>
                  <div>
                    <h6 className="fw-semibold mb-1">Delivery Verification</h6>
                    <p className="small text-muted mb-0">
                      Always verify customer address and collect digital confirmation.
                    </p>
                  </div>
                </div>
                
                <div className="d-flex gap-3">
                  <div className="flex-shrink-0">
                    <div className="bg-warning bg-opacity-10 p-2 rounded">
                      <FileText size={16} className="text-warning" />
                    </div>
                  </div>
                  <div>
                    <h6 className="fw-semibold mb-1">Incident Reporting</h6>
                    <p className="small text-muted mb-0">
                      Report any issues or incidents immediately through support channels.
                    </p>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col xs={12} lg={6}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Header className="bg-transparent border-0 pb-0">
              <h5 className="fw-semibold">Resources & Support</h5>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col xs={6}>
                  <Button 
                    variant="outline-primary" 
                    href="/privacy"
                    className="w-100 d-flex align-items-center gap-2"
                    size="sm"
                  >
                    <Shield size={16} />
                    Privacy Policy
                  </Button>
                </Col>
                <Col xs={6}>
                  <Button 
                    variant="outline-success" 
                    href="/terms"
                    className="w-100 d-flex align-items-center gap-2"
                    size="sm"
                  >
                    <FileText size={16} />
                    Terms & Conditions
                  </Button>
                </Col>
                <Col xs={6}>
                  <Button 
                    variant="outline-info" 
                    href="/support"
                    className="w-100 d-flex align-items-center gap-2"
                    size="sm"
                  >
                    <UserCheck size={16} />
                    Contact Support
                  </Button>
                </Col>
                <Col xs={6}>
                  <Button 
                    variant="outline-danger" 
                    href="/deactivate"
                    className="w-100 d-flex align-items-center gap-2"
                    size="sm"
                  >
                    <AlertTriangle size={16} />
                    Deactivate Account
                  </Button>
                </Col>
              </Row>
              
              <div className="mt-4 p-3 bg-light rounded">
                <h6 className="fw-semibold mb-2">Need Help?</h6>
                <p className="small text-muted mb-2">
                  Our support team is available 24/7 to assist with any delivery issues or questions.
                </p>
                <Button variant="link" href="/support" className="p-0 small">
                  Contact Support →
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

// Add this CSS to your global styles or use styled-components
const customStyles = `
.gradient-bg {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.hero-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.stats-card {
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
}

.shadow-soft {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}
`;

// You can add the customStyles to your global CSS or use a CSS-in-JS solution